import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import SSOConfiguration from './ssoconfigurationform';

import { getSSODetails } from '../../api/ssoApi';
import { getUsersList } from '../../api/user';

// The temporary access key
const SECRET_ACCESS_KEY = 'A7X9Q2M4L8Z1';

const SSOAccess = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [accessGranted, setAccessGranted] = useState(false);

  const [showWarning, setShowWarning] = useState(false);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);

  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const userRole = sessionStorage.getItem('access');

  useEffect(() => {
    checkAccessRequirements();
  }, []);

  const checkAccessRequirements = async () => {
    setLoading(true);

    try {
      //  Check if SSO is already configured
      const ssoRes = await getSSODetails();

      if (!ssoRes.isError && ssoRes.data) {
        const { sso, verified } = ssoRes.data;

        // If 'sso' is "enabled" or "disabled", it means setup was done before -> ALLOW.
        // If 'verified' is true, it means setup is in progress -> ALLOW.
        // If 'verified' is false, it means setup is in progress(configured but not verified) -> ALLOW.
        if (sso === 'enabled' || sso === 'disabled' || verified === true || verified === false) {
          setAccessGranted(true);
          setLoading(false);
          return;
        }
      }

      // If NOT configured , check User Count
      const userRes = await getUsersList();

      const userList = userRes.data && Array.isArray(userRes.data) ? userRes.data : [];

      if (userList.length <= 1 || userRole === 'admin') {
        // Single user (Account Admin) -> Direct Access
        // For admin users, as for them SSO is not accessible just a access restricted message will be shown in SSO configuration form
        setAccessGranted(true);
      } else {
        // Multiple users -> Show "Restricted" Warning
        setShowWarning(true);
      }
    } catch (error) {
      console.error('Error checking SSO requirements', error);
      setShowWarning(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Navigate back to Settings
    navigate('/settings');
  };

  const handleProceedFromWarning = () => {
    setShowWarning(false);
    setShowPasswordPrompt(true);
  };

  const handlePasswordSubmit = () => {
    if (passwordInput === SECRET_ACCESS_KEY) {
      setShowPasswordPrompt(false);
      setAccessGranted(true);
    } else {
      setPasswordError('Incorrect access key. Please contact support.');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (accessGranted) {
    return <SSOConfiguration />;
  }

  return (
    <Box>
      {/* Warning Dialog */}

      <Dialog open={showWarning} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>SSO Access Restricted</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 2, fontWeight: 500 }}>
            To configure SSO, please contact the Silzila support team at <b>support@silzila.com</b>.
            <br />
            We are happy to help you configure SSO.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '16px' }}>
          <Button
            onClick={handleCancel}
            variant="outlined"
            sx={{
              position: 'relative',
              overflow: 'hidden',
              backgroundColor: 'white',
              color: 'inherit',
              borderColor: '#808080',
              textTransform: 'none',
              zIndex: 1,

              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '0%',
                height: '100%',
                backgroundColor: '#808080',
                transition: 'width 0.3s ease',
                zIndex: -1,
              },

              '&:hover::before': {
                width: '100%',
              },

              '&:hover': {
                color: '#fff',
                backgroundColor: 'transparent',
                borderColor: '#808080',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleProceedFromWarning}
            variant="outlined"
            sx={{
              position: 'relative',
              overflow: 'hidden',
              backgroundColor: 'white',
              color: '#2bb9bb',
              borderColor: '#2bb9bb',
              textTransform: 'none',
              zIndex: 1,

              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '0%',
                height: '100%',
                backgroundColor: '#2bb9bb',
                transition: 'width 0.3s ease',
                zIndex: -1,
              },

              '&:hover::before': {
                width: '100%',
              },

              '&:hover': {
                color: 'white',
                borderColor: '#2bb9bb',
                backgroundColor: 'transparent',
              },
            }}
          >
            Proceed
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Prompt Dialog */}

      <Dialog open={showPasswordPrompt} maxWidth="xs" fullWidth>
        <DialogTitle>Enter Access Key</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please enter the access key provided by the support team.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="password"
            label="Access Key"
            variant="outlined"
            size="small"
            value={passwordInput}
            onChange={(e) => {
              setPasswordInput(e.target.value);
              setPasswordError('');
            }}
            error={!!passwordError}
            helperText={passwordError}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && passwordInput) handlePasswordSubmit();
            }}
          />
        </DialogContent>
        <DialogActions sx={{ padding: '16px' }}>
          <Button onClick={handleCancel} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handlePasswordSubmit}
            variant="contained"
            disabled={!passwordInput}
            sx={{
              textTransform: 'none',
              color: '#fff',
              '&:hover': {
                backgroundColor: 'primary.main',
                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)',
              },
            }}
          >
            Verify
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SSOAccess;
